// React core
import { FunctionComponent, useState, useEffect } from "react";

// Interfaces & types
import I_BaseAPIResponse from "src/common_interfaces/I_BaseAPIResponse";
import {
  I_FetchPortalAPIGeneratedPDFResponse,
  I_FetchPortalAPIGeneratedPDFRequest,
} from "./I_FetchPortalAPIGeneratedPDF";

// API & utilities
import getPortalAPIGeneratedPDF from "./crud/GetPortalAPIGeneratedPDF";
import base64ToFile from "src/utilities/base64ToFile";

// UI components & styles
import LDSEllipsis from "../LDS/LDSEllipsis/LDSEllipsis";
import "./FetchPortalAPIGeneratedPDF.css";

interface Props {
  pdfData: I_FetchPortalAPIGeneratedPDFRequest;
  fetchFn?: typeof getPortalAPIGeneratedPDF;
}

/**
 * FetchPortalAPIGeneratedPDF component
 * ------------------
 * Responsible for:
 *  - Requesting a PDF from the Portal API PDF Generator
 *  - Converting Base64 response into a File
 *  - Creating and cleaning up an object URL for preview
 *  - Rendering loading, error, and success states
 */
const FetchPortalAPIGeneratedPDF: FunctionComponent<Props> = ({
  pdfData,
  fetchFn = getPortalAPIGeneratedPDF,
}) => {
  const { template, data } = pdfData;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfResponse, setPdfResponse] =
    useState<I_BaseAPIResponse<I_FetchPortalAPIGeneratedPDFResponse> | null>(
      null
    );
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  /**
   * Effect: Fetch PDF when template or data changes
   */
  useEffect(() => {
    const fetchPDF = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await fetchFn({ template, data });
        setPdfResponse(res);
      } catch (err: unknown) {
        console.error("Failed to fetch PDF:", err);
        setError("Failed to fetch PDF");
      } finally {
        setLoading(false);
      }
    };

    fetchPDF();
  }, [template, data, fetchFn]);

  /**
   * Effect: Create object URL when a valid PDF response arrives.
   * Cleans up the URL when component unmounts or response changes.
   */
  useEffect(() => {
    if (pdfResponse && !pdfResponse.HasError && pdfResponse.Data?.PdfBase64) {
      const file = base64ToFile(
        pdfResponse.Data.PdfBase64,
        "file-preview.pdf"
      );
      const url = URL.createObjectURL(file);
      setPdfUrl(url);

      return () => {
        URL.revokeObjectURL(url);
        setPdfUrl(null);
      };
    }
  }, [pdfResponse]);

  return (
    <div className="FetchPortalAPIGeneratedPDF">
      {/* Loading state */}
      {loading && (
        <div className="FetchPortalAPIGeneratedPDFLoading">
          <p>Fetching PDF…</p>
          <LDSEllipsis />
        </div>
      )}

      {/* PDF Preview */}
      {pdfUrl && (
        <iframe
          src={pdfUrl}
          title="PDF Viewer"
          style={{ width: "100%", height: "100%", border: "none" }}
        />
      )}

      {/* Generic error */}
      {error && (
        <div className="FetchPortalAPIGeneratedPDFError">
          <p>{error}</p>
        </div>
      )}

      {/* API error response */}
      {pdfResponse?.HasError && (
        <div className="FetchPortalAPIGeneratedPDFErrorWithResponse">
          <p>{pdfResponse.ErrorMessage}</p>
        </div>
      )}
    </div>
  );
};

export default FetchPortalAPIGeneratedPDF;
